import * as React from 'react';
import FileSaver from 'file-saver';
import moment from 'moment';
import { Certification } from './Certification';
import { downloadDocument } from '../../slices/documents';
import { fetchCertifications } from '../../slices/certifications';
import { fetchSubscriptions } from '../../slices/subscriptionsV2';
import { getFileNameFromContentDisposition } from '../../helpers/helpers';
import { ICertification, VerificationCategory } from '../../interfaces/certifications';
import { regenerateCertificate } from '../../slices/regenerateCertificate';
import { useAppDispatch, useTypedSelector } from '../../combineReducers';
import { useParams } from 'react-router-dom';
import { usePermission } from '../../hooks/usePermission';
import { useTranslation } from 'react-i18next';
import { Widget } from '../Widget';

type RouteParams = {
  organizationId: string;
};

export const Certifications: React.FC = () => {
  const { organizationId } = useParams<RouteParams>();
  const { t } = useTranslation('subscriber');
  const dispatch = useAppDispatch();
  const { hasAccreditationsPermission } = usePermission();
  const { certifications, isFetching: isFetchingCertifications, error } = useTypedSelector(
    state => state.certifications
  );
  const { subscriptions, isFetching: isFetchingSubscriptions } = useTypedSelector(state => state.subscriptionsV2);
  const { mainSubscription } = useTypedSelector(state => state.mainSubscription);

  const certificationsItems = certifications.map(certification => {
    const matching = subscriptions.find(subscription => subscription.id === certification.subscriptionId);
    const isExpired = matching?.expirationDate ? moment(matching.expirationDate).isBefore() : false;
    return {
      ...certification,
      isExpired
    };
  });

  React.useEffect(() => {
    dispatch(fetchCertifications(organizationId));
    dispatch(fetchSubscriptions(organizationId));
  }, [dispatch, organizationId]);

  const handleDownload = (certification: ICertification) => () => {
    const { documentReferenceId, name, verificationCategory } = certification;
    const certificateName = name
      .replace('CHAS', '')
      .trim()
      .replace(/ /g, '_');
    const documentName = `Certificate_${certificateName}_${
      verificationCategory === VerificationCategory.Assured ? 'Assured_' : ''
    }${mainSubscription.countryISO2}`;

    dispatch(
      downloadDocument({
        documentReferenceId,
        afterSuccess: ({ data, headers }) => {
          const blob = new Blob([data], { type: headers['content-type'] });
          const originalFileName = getFileNameFromContentDisposition(headers['content-disposition']);

          FileSaver.saveAs(blob, `${documentName}.${originalFileName.extension}`);
        }
      })
    );
  };

  const handleRegenerate = (accreditationId: string): void => {
    dispatch(regenerateCertificate(accreditationId));
  };

  if (!hasAccreditationsPermission || (certifications.length === 0 && !isFetchingCertifications)) return null;

  return (
    <Widget
      title={t('newDashboard.certifications.title', 'Certifications')}
      isFetching={isFetchingCertifications || isFetchingSubscriptions}
      hasFetchingError={!!error}
    >
      {certificationsItems?.map(certification => (
        <Certification
          key={certification.id}
          certification={certification}
          onDownloadClick={handleDownload(certification)}
          onRefreshClick={handleRegenerate}
        />
      ))}
    </Widget>
  );
};
